import { ref, onMounted, onBeforeUnmount } from 'vue'

/**
 * Prevents pull-to-refresh on iOS when user pulls down at top of scroll.
 * Uses touch handlers scoped to the container - does not affect parent document.
 * Header-based approach: always prevent when touch starts on header and user pulls down.
 * @param {import('vue').Ref<HTMLElement|null>} containerRef - Ref to the wrapper that contains .mobile-view
 */
export function usePreventPullToRefresh(containerRef) {
  const touchStartY = ref(0)
  const touchStartedOnHeader = ref(false)

  function handleTouchStart(e) {
    if (e.touches?.length) {
      touchStartY.value = e.touches[0].clientY
      const container = containerRef.value
      const mobileView = container?.querySelector('.mobile-view') || container?.firstElementChild
      const headerEl = mobileView?.querySelector('.header')
      touchStartedOnHeader.value = !!(headerEl && headerEl.contains(e.target))
    }
  }

  function handleTouchMove(e) {
    if (e.touches?.length !== 1) return

    const container = containerRef.value
    if (!container) return

    const mobileView = container.querySelector('.mobile-view') || container.firstElementChild
    if (!mobileView) return

    const currentY = e.touches[0].clientY
    const pullingDown = currentY > touchStartY.value
    const pullingUp = currentY < touchStartY.value

    // Header approach: touch started on header + pull down → always prevent (no scroll math)
    const headerEl = mobileView.querySelector('.header')
    const touchOnHeader = headerEl && headerEl.contains(e.target)
    if ((touchStartedOnHeader.value || touchOnHeader) && pullingDown) {
      e.preventDefault()
      return
    }

    // Content/footer: use scroll position for content area and bottom overscroll
    let scrollEl = mobileView
    const target = e.target
    const contentEl = mobileView.querySelector('.content')
    if (contentEl && contentEl.contains(target) && typeof contentEl.scrollTop === 'number') {
      scrollEl = contentEl
    }

    const atTop = scrollEl.scrollTop <= 0
    const atBottom =
      scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1

    if ((atTop && pullingDown) || (atBottom && pullingUp)) {
      e.preventDefault()
    }
  }

  onMounted(() => {
    const el = containerRef.value
    if (!el) return

    // Capture phase is critical for Safari: we must intercept before Safari handles the gesture
    el.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true })
    el.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true })
  })

  onBeforeUnmount(() => {
    const el = containerRef.value
    if (!el) return

    el.removeEventListener('touchstart', handleTouchStart, { capture: true })
    el.removeEventListener('touchmove', handleTouchMove, { capture: true })
  })
}
